import ApiService from 'api-client/ApiService'
import config from 'app-config'
import { NetworkDetailsResponse, NetworksResponse } from 'openstack/components/networks/model'

class Neutron extends ApiService {
  public getClassName() {
    return 'neutron'
  }

  protected async getEndpoint() {
    return this.client.keystone.getServiceEndpoint('neutron', 'public')
  }

  /**
   * Returns the base URL for the Neutron API
   */
  get baseURL(): string {
    if (location.hostname === 'localhost') {
      return `${config.apiHost}/pf9/neutron`
    }

    return `${location.origin}/pf9/neutron`
  }

  async setRegionUrls() {
    const services = (await this.client.keystone.getServiceCatalog()).find(
      (x) => x.name === 'neutron',
    ).endpoints
    return services.reduce((accum, service) => {
      accum[service.region] = `${service.url}/v2.0`
      return accum
    }, {})
  }

  async getApis() {
    const response = await this.client.basicGet<any>({
      url: '/',
      options: {
        clsName: this.getClassName(),
        mthdName: 'getOpenstackNetworkApis',
      },
    })
    return response
  }

  async getNetwork(id) {
    const response = await this.client.basicGet<NetworkDetailsResponse>({
      url: `/v2.0/networks/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getOpenstackNetwork',
      },
    })
    return response.network
  }

  // When allTenants is true, ask the backend for networks across every tenant
  // (admin-only; backend returns 403 otherwise) instead of scoping to the active
  // project. all_tenants=true overrides any project_id, so we omit project_id.
  async getNetworks(allTenants = false) {
    const query = allTenants ? 'all_tenants=true' : `project_id=${this.client.activeProjectId}`
    const response = await this.client.basicGet<NetworksResponse>({
      url: `/v2.0/networks?${query}`,
      endpoint: this.baseURL,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getOpenstackNetworks',
      },
    })
    return response.networks
  }

  // Fetch networks scoped to a specific project. includeShared=false returns only
  // tenant-owned networks (project_id match); true also includes shared networks.
  async getNetworksByProject(projectId: string, includeShared: boolean = true) {
    const response = await this.client.basicGet<NetworksResponse>({
      url: `/v2.0/networks?project_id=${projectId}`,
      endpoint: this.baseURL,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getOpenstackNetworksByProject',
      },
    })
    if (includeShared) {
      return response.networks.filter(
        (net: any) => net.project_id === projectId || net.shared === true,
      )
    }
    return response.networks.filter((net: any) => net.project_id === projectId)
  }

  async createNetwork(body) {
    const response = await this.client.basicPost<NetworkDetailsResponse>({
      url: `/v2.0/networks`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createOpenstackNetwork',
      },
    })
    return response.network
  }

  async deleteNetwork(id) {
    return this.client.basicDelete<any>({
      url: `/v2.0/networks/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'deleteOpenstackNetwork',
      },
    })
  }

  async updateNetwork({ id, body }) {
    const url = `/v2.0/networks/${id}`
    const response = await this.client.basicPut<NetworkDetailsResponse>({
      url,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateOpenstackNetwork',
      },
    })
    return response.network
  }

  /**
   * Add tags to a network using OpenStack Network Tags API
   * API Reference: https://docs.openstack.org/api-ref/network/v2/index.html#create-tags
   * @param networkId - The UUID of the network
   * @param tags - Array of tag strings to add
   */
  async addNetworkTags(networkId: string, tags: string[]) {
    const url = `/v2.0/networks/${networkId}/tags`
    await this.client.basicPut<any>({
      url,
      body: { tags },
      options: {
        clsName: this.getClassName(),
        mthdName: 'addNetworkTags',
      },
    })
  }

  async getSubnet(id) {
    const response = await this.client.basicGet<any>({
      url: `/v2.0/subnets/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getOpenstackSubnet',
      },
    })
    return response.subnet
  }

  async getSubnets(projectId?: string) {
    const url = projectId ? `/v2.0/subnets?project_id=${projectId}` : `/v2.0/subnets`
    const response = await this.client.basicGet<any>({
      url,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getOpenstackSubnets',
      },
    })
    return response.subnets
  }

  async createSubnet(body) {
    const response = await this.client.basicPost<any>({
      url: `/v2.0/subnets`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createOpenstackSubnet',
      },
    })
    return response.subnet
  }

  async updateSubnet({ id, body }) {
    const url = `/v2.0/subnets/${id}`
    const response = await this.client.basicPut<any>({
      url,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateOpenstackSubnet',
      },
    })
    return response.subnet
  }

  async deleteSubnet(id) {
    return this.client.basicDelete<any>({
      url: `/v2.0/subnets/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'deleteOpenstackSubnet',
      },
    })
  }

  async getAllAvailablePorts(network_id = '', query_params = '') {
    const currentTenant = this.client.activeProjectId
    let url = network_id
      ? `/v2.0/ports?&network_id=${network_id}&project_id=${currentTenant}&device_id=&device_owner=`
      : `/v2.0/ports?project_id=${currentTenant}&device_id=&device_owner=`

    if (query_params) {
      url += '&' + query_params
    }

    const detailResponse = await this.client.basicGet<any>({
      url,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getOpenstackPorts',
      },
    })

    return detailResponse.ports
  }

  async getPortByNetworkIdCount(network_id = '', query_params = '') {
    const currentTenant = this.client.activeProjectId
    let url = network_id
      ? `/v2.0/ports?&network_id=${network_id}&project_id=${currentTenant}&counts_only=true`
      : `/v2.0/ports?project_id=${currentTenant}&counts_only=true`

    if (query_params) {
      url += '&' + query_params
    }

    const countResponse = await this.client.basicGet<any>({
      endpoint: this.baseURL,
      url: url,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getOpenstackPorts',
      },
    })

    return countResponse.ports_count
  }

  async getPortByNetworkId(network_id = '', query_params = '') {
    const currentTenant = this.client.activeProjectId
    let url = network_id
      ? `/v2.0/ports?&network_id=${network_id}&project_id=${currentTenant}`
      : `/v2.0/ports?project_id=${currentTenant}`

    if (query_params) {
      url += '&' + query_params
    }

    const detailResponse = await this.client.basicGet<any>({
      endpoint: this.baseURL,
      url,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getOpenstackPorts',
      },
    })

    return detailResponse.ports
  }

  // CQRS API call to get a single port
  async getPortById(port_id: string) {
    const detailResponse = await this.client.basicGet<any>({
      endpoint: this.baseURL,
      url: `/v2.0/ports?uuid=${port_id}&project_id=${this.client.activeProjectId}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getOpenstackPorts',
      },
    })
    return detailResponse?.ports[0] || {}
  }

  async getPortsForVm({ vmId }) {
    const response = await this.client.basicGet<any>({
      url: `/v2.0/ports?device_id=${vmId}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getOpenstackPortsForVm',
      },
    })
    return response.ports
  }

  // --- Metadata (cloud-init) service health ---
  // Each network is expected to have a distributed port; a network without one has broken
  // cloud-init metadata routing. Both queries are admin-only (all_tenants=true) and the
  // device_owner filter is backed by pf9-cqrs PR #98.
  // See MetadataServiceHealth.spec.md (MSH-FR-001).

  // Total count of networks across all tenants (counts_only avoids fetching the full list
  // for the summary). Response field confirmed in pf9-cqrs pkg/neutron/types/networks.go.
  async getAllNetworksCount() {
    const response = await this.client.basicGet<any>({
      url: `/v2.0/networks?all_tenants=true&counts_only=true`,
      endpoint: this.baseURL,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getOpenstackNetworksCount',
      },
    })
    return response.networks_count
  }

  // Count of distributed (metadata) ports across all tenants = networks with working
  // metadata routing.
  async getDistributedPortsCount() {
    const response = await this.client.basicGet<any>({
      url: `/v2.0/ports?device_owner=network:distributed&all_tenants=true&counts_only=true`,
      endpoint: this.baseURL,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getOpenstackDistributedPortsCount',
      },
    })
    return response.ports_count
  }

  // Full list of distributed (metadata) ports across all tenants. Used only when the admin
  // expands the card to see exactly which networks are broken.
  async getDistributedPorts() {
    const response = await this.client.basicGet<any>({
      url: `/v2.0/ports?device_owner=network:distributed&all_tenants=true`,
      endpoint: this.baseURL,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getOpenstackDistributedPorts',
      },
    })
    return response.ports
  }

  async getPort(id) {
    const response = await this.client.basicGet<any>({
      url: `/v2.0/ports/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getOpenstackPort',
      },
    })
    return response.port
  }

  async createPort(body) {
    const url = `/v2.0/ports`
    const response = await this.client.basicPost<any>({
      url,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createPort',
      },
    })
    return response.port
  }

  async deletePort(id) {
    const url = `/v2.0/ports/${id}`
    await this.client.basicDelete<any>({
      url,
      options: {
        clsName: this.getClassName(),
        mthdName: 'deletePort',
      },
    })
  }

  async updatePort({ id, body }) {
    const url = `/v2.0/ports/${id}`
    const response = await this.client.basicPut<any>({
      url,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updatePort',
      },
    })
    return response.port
  }

  async getPortsByProject(projectId: string) {
    const response = await this.client.basicGet<any>({
      url: `/v2.0/ports?project_id=${projectId}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getPortsByProject',
      },
    })
    return response.ports
  }

  async getFloatingIps(projectId?: string) {
    const url = projectId ? `/v2.0/floatingips?project_id=${projectId}` : `/v2.0/floatingips`
    const response = await this.client.basicGet<any>({
      url,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getFloatingIps',
      },
    })
    return response.floatingips
  }

  async getFloatingIp(id) {
    const response = await this.client.basicGet<any>({
      url: `/v2.0/floatingips/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getFloatingIp',
      },
    })
    return response.floatingip
  }

  async createFloatingIp(body) {
    const response = await this.client.basicPost<any>({
      url: `/v2.0/floatingips`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createFloatingIp',
      },
    })
    return response.floatingip
  }

  async updateFloatingIp({ id, body }) {
    const response = await this.client.basicPut<any>({
      url: `/v2.0/floatingips/${id}`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateFloatingIp',
      },
    })
    return response.floatingip
  }

  async detachFloatingIp(id) {
    const response = await this.client.basicPut<any>({
      url: `/v2.0/floatingips/${id}`,
      body: {
        floatingip: { port_id: null },
      },
      options: {
        clsName: this.getClassName(),
        mthdName: 'detachFloatingIp',
      },
    })
    return response.floatingip
  }

  async deleteFloatingIp(id) {
    return this.client.basicDelete<any>({
      url: `/v2.0/floatingips/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'deleteFloatingIp',
      },
    })
  }

  async getNetworkIpAvailability() {
    const url = `/v2.0/network-ip-availabilities`
    const response = await this.client.basicGet<any>({
      url,
      options: {
        clsName: this.getClassName(),
        mthdName: 'networkIpAvailabilities',
      },
    })
    return response.network_ip_availabilities
  }

  async getSharedSecurityGroups() {
    const response = await this.client.basicGet<any>({
      url: `/v2.0/security-groups?shared=True`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getSharedSecurityGroups',
      },
    })
    return response?.security_groups
  }

  async getSecurityGroups(projectId: string = this.client.activeProjectId) {
    const url = `/v2.0/security-groups?project_id=${projectId}`
    const response = await this.client.basicGet<any>({
      url,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getSecurityGroups',
      },
    })
    return response.security_groups
  }

  async getSecurityGroup(id) {
    const response = await this.client.basicGet<any>({
      url: `/v2.0/security-groups/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getSecurityGroup',
      },
    })
    return response.security_group
  }

  async createSecurityGroup(body) {
    const response = await this.client.basicPost<any>({
      url: `/v2.0/security-groups`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createSecurityGroup',
      },
    })
    return response.security_group
  }

  async updateSecurityGroup({ id, body }) {
    const response = await this.client.basicPut<any>({
      url: `/v2.0/security-groups/${id}`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateSecurityGroup',
      },
    })
    return response.security_group
  }

  async deleteSecurityGroup(id) {
    await this.client.basicDelete<any>({
      url: `/v2.0/security-groups/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'deleteSecurityGroup',
      },
    })
  }

  async getSecurityGroupRules() {
    const url = `/v2.0/security-group-rules`
    const response = await this.client.basicGet<any>({
      url,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getSecurityGroupRules',
      },
    })
    return response.security_group_rules
  }

  async createSecurityGroupRule(params) {
    const url = `/v2.0/security-group-rules`
    const response = await this.client.basicPost<any>({
      url,
      body: {
        security_group_rule: params,
      },
      options: {
        clsName: this.getClassName(),
        mthdName: 'createSecurityGroupRule',
      },
    })
    return response.security_group_rule
  }

  async createSecurityGroupRules(body) {
    const response = await this.client.basicPost<any>({
      url: `/v2.0/security-group-rules`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createSecurityGroupRules',
      },
    })
    return response.security_group_rules
  }

  async deleteSecurityGroupRule(id) {
    await this.client.basicDelete<any>({
      url: `/v2.0/security-group-rules/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'deleteSecurityGroupRule',
      },
    })
  }

  async getRouter(id) {
    const response = await this.client.basicGet<any>({
      url: `/v2.0/routers/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getOpenstackRouter',
      },
    })
    return response.router
  }

  async getRouters(projectId?: string) {
    const url = projectId ? `/v2.0/routers?project_id=${projectId}` : `/v2.0/routers`
    const response = await this.client.basicGet<any>({
      url,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getOpenstackRouters',
      },
    })
    return response.routers
  }

  async createRouter(body) {
    const response = await this.client.basicPost<any>({
      url: `/v2.0/routers`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createOpenstackRouter',
      },
    })
    return response.router
  }

  async updateRouter({ id, body }) {
    const url = `/v2.0/routers/${id}`
    const response = await this.client.basicPut<any>({
      url,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateOpenstackRouter',
      },
    })
    return response.router
  }

  async deleteRouter(id) {
    return this.client.basicDelete<any>({
      url: `/v2.0/routers/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'deleteOpenstackRouter',
      },
    })
  }

  async addInterface({ id, body }) {
    const url = `/v2.0/routers/${id}/add_router_interface`
    const response = await this.client.basicPut<any>({
      url,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'addInterface',
      },
    })
    return response.data
  }

  async removeInterface({ id, body }) {
    const url = `/v2.0/routers/${id}/remove_router_interface`
    const response = await this.client.basicPut<any>({
      url,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'removeInterface',
      },
    })
    return response.data
  }

  async getAllQuotas() {
    const url = `/v2.0/quotas`
    const response = await this.client.basicGet<any>({
      url,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getAllQuotas',
      },
    })
    return response.quotas
  }

  async getNetworkQuotas({ id }) {
    const response = await this.client.basicGet<any>({
      url: `/v2.0/quotas/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getNetworkQuotas',
      },
    })
    return response.quota
  }

  async getNetworkQuotaUsage({ id }) {
    const response = await this.client.basicGet<any>({
      url: `/v2.0/quotas/${id}/details.json`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getNetworkQuotaUsage',
      },
    })
    return response.quota
  }

  async updateNetworkQuotas({ id, body }) {
    const response = await this.client.basicPut<any>({
      url: `/v2.0/quotas/${id}`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateNetworkQuotas',
      },
    })
    return response.quota
  }

  async getProjectQuotaForRegion(id, region) {
    const endpoint = `${(await this.setRegionUrls())[region]}`
    const url = `/quotas/${id}`
    const response = await this.client.basicGet<any>({
      endpoint,
      url,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getProjectQuotaForRegion',
      },
    })
    return response.quota
  }

  async getDefaultQuotasForRegion(region) {
    const projectId = (await this.client.keystone.getProjects())[0]
    const endpoint = `${(await this.setRegionUrls())[region]}`
    const url = `/quotas/${projectId}`
    const response = await this.client.basicGet<any>({
      endpoint,
      url,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getDefaultQuotasForRegion',
      },
    })
    return response.quota
  }

  async setQuotas(projectId, params) {
    const url = `/v2.0/quotas/${projectId}`
    const response = await this.client.basicPut<any>({
      url,
      body: {
        quota: params,
      },
      options: {
        clsName: this.getClassName(),
        mthdName: 'setQuotas',
      },
    })
    return response.quota
  }

  async setQuotasForRegion(projectId, region, params) {
    const endpoint = `${(await this.setRegionUrls())[region]}`
    const url = `/quotas/${projectId}`
    const response = await this.client.basicPut<any>({
      endpoint,
      url,
      body: {
        quota: params,
      },
      options: {
        clsName: this.getClassName(),
        mthdName: 'setQuotasForRegion',
      },
    })
    return response.quota
  }

  async getNetworkStoarageDetails() {
    const url = `/v2.0/agents`
    const response = await this.client.basicGet<any>({
      url,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getNetworkStoarageDetails',
      },
    })
    return response.agents
  }

  async checkHealth() {
    return this.client.basicGet<any>({
      url: `/`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'checkHealth',
      },
    })
  }

  // Network RBAC Policies
  async getNetworkRBACPolicies() {
    const url = `/v2.0/rbac-policies?action=access_as_shared`
    const response = await this.client.basicGet<any>({
      url,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getNetworkRBACPolicies',
      },
    })
    return response.rbac_policies
  }

  async createNetworkRBACPolicy({ body }) {
    const url = `/v2.0/rbac-policies`
    const response = await this.client.basicPost<any>({
      url,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createNetworkRBACPolicy',
      },
    })
    return response.rbac_policies
  }

  async deleteNetworkRBACPolicy({ policyId }) {
    const url = `/v2.0/rbac-policies/${policyId}`
    await this.client.basicDelete<any>({
      url,
      options: {
        clsName: this.getClassName(),
        mthdName: 'deleteNetworkRBACPolicy',
      },
    })
  }

  async getPortsForSubnets({ subnetIds = [] }) {
    let url = `/v2.0/ports?project_id=${this.client.activeProjectId}&device_owner=network:router_interface&status=ACTIVE&`
    url += subnetIds.map((id) => `fixed_ips=subnet_id=${id}`).join('&')
    const response = await this.client.basicGet<any>({
      url,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getPortsForSubnets',
      },
    })
    return response.ports
  }

  async getPortsForRouter({ routerId }) {
    if (!routerId) {
      return []
    }

    const url = `/v2.0/ports?device_id=${routerId}`
    const response = await this.client.basicGet<any>({
      url,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getPortsForRouter',
      },
    })
    return response.ports
  }
}

export default Neutron
